import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './style.scss';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Checkbox,
  FormControlLabel
} from "@mui/material";

const Messages = () => {
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loadingId, setLoadingId] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);

  const token = localStorage.getItem("token");
  const BaseUrl = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    axios
      .get(`${BaseUrl}/messages`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(response => {
        setMessages(response.data.messages.reverse());
      })
      .catch(() => {
        setError('Failed to fetch messages. Please try again later.');
      });
  }, [BaseUrl, token]);

  // Single delete
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this message?")) return;
    try {
      setLoadingId(id);
      await axios.delete(`https://eventscons.com/eventbackendnew/api/messages/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages(prev => prev.filter(msg => msg.id !== id));
    } catch {
      alert("Failed to delete the message. Please try again.");
    } finally {
      setLoadingId(null);
    }
  };

  // Bulk delete
  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      alert("Please select at least one message to delete.");
      return;
    }
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} messages?`)) return;

    console.log("Deleting IDs:", selectedIds);

    try {
      setBulkLoading(true);

      // First try DELETE with data
      try {
        await axios.delete("https://eventscons.com/eventbackendnew/api/messages", {
          headers: { Authorization: `Bearer ${token}` },
          data: { ids: selectedIds },
        });
      } catch (err) {
        // If DELETE fails, fallback to POST
        await axios.post(
          "https://eventscons.com/eventbackendnew/api/messages",
          { ids: selectedIds },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      setMessages(prev => prev.filter(msg => !selectedIds.includes(msg.id)));
      setSelectedIds([]);
    } catch {
      alert("Failed to bulk delete messages. Please try again.");
    } finally {
      setBulkLoading(false);
    }
  };

  // Toggle checkbox
  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  // Select All
  const toggleSelectAll = () => {
    if (selectedIds.length === messages.length) {
      setSelectedIds([]); // unselect all
    } else {
      setSelectedIds(messages.map(msg => msg.id));
    }
  };

  // Render field only if value exists
  const renderField = (label, fieldValue) => {
    if (fieldValue && (typeof fieldValue === 'string' ? fieldValue.trim() !== "" : true)) {
      return <p><strong>{label}:</strong> {fieldValue}</p>;
    }
    return null;
  };

  return (
    <div className="messages-container">
      <h2 style={{ color: "#9B1321" }}>Messages</h2>

      {/* Bulk actions */}
      {messages.length > 0 && (
        <div style={{ marginBottom: "15px" }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedIds.length === messages.length}
                onChange={toggleSelectAll}
              />
            }
            label="Select All"
          />
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={handleBulkDelete}
            disabled={bulkLoading || selectedIds.length === 0}
            style={{ marginLeft: "10px" }}
          >
            {bulkLoading ? "Deleting..." : `Delete Selected (${selectedIds.length})`}
          </Button>
        </div>
      )}

      {error && <p className="error-message">{error}</p>}
      {messages.length === 0 ? (
        <Typography variant="body1" color="textSecondary">
          No messages available.
        </Typography>
      ) : (
        <Grid container spacing={3}>
          {messages.map((msg) => (
            <Grid item xs={12} key={msg.id}>
              <Card variant="outlined">
                <CardContent>
                  {/* Checkbox for selection */}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedIds.includes(msg.id)}
                        onChange={() => toggleSelect(msg.id)}
                      />
                    }
                    label="Select"
                  />

                  {renderField("Subject", msg.subject)}
                  {renderField("Name", msg.name)}
                  {renderField("Email", msg.email)}
                  {renderField("Phone", msg.phone)}
                  {renderField("Company Name", msg.companyName)}
                  {renderField("Event Name", msg.eventName)}
                  {renderField("Event Type", msg.eventType)}
                  {renderField("Preferred Date", msg.preferredDate)}
                  {renderField("Event Duration", msg.eventDuration)}
                  {renderField("Expected Attendees", msg.expectedAttendees)}
                  {renderField("Venue Location", msg.venueLocation)}
                  {renderField("Has Venue", msg.hasVenue)}
                  {renderField("Venue Name", msg.venueName)}
                  {renderField("Services Required", msg.servicesRequired)}
                  {renderField("Additional Comments", msg.additionalComments)}

                  <Typography
                    variant="caption"
                    style={{
                      marginTop: "8px",
                      display: "block",
                      color: "#9B1321"
                    }}
                  >
                    Sent at: {new Date(msg.created_at).toLocaleString()}
                  </Typography>

                  {/* Single delete button */}
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    style={{ marginTop: "10px" }}
                    onClick={() => handleDelete(msg.id)}
                    disabled={loadingId === msg.id}
                  >
                    {loadingId === msg.id ? "Deleting..." : "Delete"}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </div>
  );
};

export default Messages;
